<script setup lang="ts">
    import { computed, watch, onMounted } from 'vue'
    import { useId, useRoute } from '@common/composables'
    import { useIndividualReportPage } from '@RankHR/Regions/pages-core'

    document.title = "Individual Report"

    const id = useId()
    const votingCycle = useRoute().params.votingCycle

    const { useIndividualReportQuery } = useIndividualReportPage()
    const { modelIndividualReportQueryV1, runIndividualReportQueryV1, authorized, redirectUrl } = useIndividualReportQuery(id, votingCycle)

    const model = computed(() => modelIndividualReportQueryV1.value)
    const personName = computed(() => modelIndividualReportQueryV1.value?.PersonName)
    const ranking = computed(() => modelIndividualReportQueryV1.value?.Ranking)
    const score = computed(() => modelIndividualReportQueryV1.value?.Score)
    const totalPossibleScore = computed(() => modelIndividualReportQueryV1.value?.TotalPossibleScore)
    const boardHighScore = computed(() => modelIndividualReportQueryV1.value?.BoardHighScore)
    const boardAverage = computed(() => modelIndividualReportQueryV1.value?.BoardAverage)
    const ratingProfile = computed(() => modelIndividualReportQueryV1.value?.RatingProfile)
    const boardRanking = computed(() => modelIndividualReportQueryV1.value?.BoardRanking)
    const ratingOverTime = computed(() => modelIndividualReportQueryV1.value?.RatingOverTime)
    const overallBoardScoringStatistics = computed(() => modelIndividualReportQueryV1.value?.OverallBoardScoringStatistics)
    const criteriaVotes = computed(() => modelIndividualReportQueryV1.value?.CriteriaVotes || [])
    const error = computed(() => modelIndividualReportQueryV1.value?.Error)

    onMounted(() => {
        runIndividualReportQueryV1()
    })

    watch(authorized, () => {
        if (!authorized.value) {
            location.href = redirectUrl.value
        }
    })

    watch(personName, () => document.title = `Report: ${personName.value}`)
</script>

<template>
    <div>
        <div id="navigationPlaceholder"></div>

        <div class="container-fluid">
            <div class="row">
                <div v-if="model" class="col-lg-12">
                    <div v-if="!error" style="margin: 30px;">
                        <div>
                            <h1 class="text-center">Multiple Management Board</h1>
                            <h2 class="text-center">Board Advisor Rating Report</h2>

                            <p class="lead">
                                <strong>Name:</strong> {{ personName }}<br />
                                <strong>Ranking:</strong> <span>{{ ranking }}</span> <br />
                                <strong>Score:</strong> <span>{{ score }}</span>
                            </p>

                            <h3 class="text-center">Rating profile</h3>
                            <br />
                            <div v-if="ratingProfile" class="table-responsive">
                                <table class="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th class="text-center" v-for="header in ratingProfile.Header.Headers">{{header}}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in ratingProfile.Rows">
                                            <td v-for="val in row.Values" class="text-center">{{val}}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <br />

                            <h3 class="text-center">Board Ranking</h3>
                            <br />
                            <div class="table-responsive">
                                <table class="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th class="text-center" v-for="header in boardRanking.Header.Headers">{{header}}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in boardRanking.Rows">
                                            <td v-for="val in row.Values" class="text-center">{{val}}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <br />

                            <h3 class="text-center">Individual's Rating Over Time</h3>
                            <br />
                            <div class="table-responsive">
                                <table class="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th class="text-center" v-for="header in ratingOverTime.Header.Headers">{{header}}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in ratingOverTime.Rows">
                                            <td v-for="val in row.Values" class="text-center">{{val}}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <br />

                            <h3 class="text-center">Overall Board Scoring Statistics</h3>
                            <br />
                            <table>
                                <tr>
                                    <td><strong>Total Possible Score:</strong></td>
                                    <td class="text-right">{{totalPossibleScore}}</td>
                                </tr>

                                <tr>
                                    <td><strong>Board High Score:</strong></td>
                                    <td class="text-right">{{boardHighScore}}</td>
                                </tr>

                                <tr>
                                    <td><strong>Board Average:</strong></td>
                                    <td class="text-right">{{boardAverage}}</td>
                                </tr>
                            </table>
                            <br />
                            <div class="table-responsive">
                                <table class="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th class="text-center" v-for="header in overallBoardScoringStatistics.Header.Headers">{{header}}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in overallBoardScoringStatistics.Rows">
                                            <td v-for="val in row.Values" class="text-center">{{val}}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <br />

                            <h3 v-if="criteriaVotes.length" class="text-center">
                                Comments for {{ personName }}
                            </h3>

                            <template v-for="criterionVotes in criteriaVotes">
                                <h4>{{criterionVotes.CriterionPath}}</h4>
                                <br />

                                <div class="table-responsive">
                                    <table class="table table-bordered table-hover">
                                        <thead>
                                            <tr>
                                                <th>Evaluator</th>
                                                <th>Score</th>
                                                <th>Comment</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="vote in criterionVotes.Votes">
                                                <td>{{vote.EvaluatorName}}</td>
                                                <td class="text-center">{{vote.Value}}</td>
                                                <td>{{vote.Comment}}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div v-else style="margin: 30px;">
                        <h1>{{error}}</h1>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>